import React from "react";
import { Link } from "react-router-dom";
import { m, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  ChevronDown,
  ClipboardCheck,
  Network,
  ShieldCheck,
} from "lucide-react";
import { BrandCopy } from "../../components/BrandWordmarkText";
import { useMarketingMotion } from "../../components/marketing/MarketingMotion";
import { cn } from "../../lib/utils";
import ConnectedSchoolConceptVisual from "./ConnectedSchoolConceptVisual";

const EASE_OUT = [0.23, 1, 0.32, 1];

// Byte-mirrors core/marketing.py DEFAULTS["why_vp360"] — change BOTH together (+ a
// _PUBLIC_CLAIM_REPLACEMENTS pair so stored CMS revisions migrate).
const FALLBACK_CONTENT = Object.freeze({
  badge: "Why Vidyapeeth360?",
  heading: "Your school does not run in modules. It runs in hand-offs.",
  subhead: "Admissions, classrooms, finance, transport and families depend on the same records — but fragmented tools make staff copy context between them. Vidyapeeth360 keeps the record, rule, owner and evidence connected, while authorised people stay in control.",
});

// POS-8/UX-06 (2026-08-18): titles speak at experience level; descs stay the qualifiers.
const FALLBACK_PILLARS = Object.freeze([
  Object.freeze({
    id: "p1",
    title: "Built on your classes, sections and people",
    desc: "Classes, sections, academic years, students, guardians and staff form the records used by the workflows your school authorises.",
  }),
  Object.freeze({
    id: "p2",
    title: "Records continue through the hand-off",
    desc: "When admissions staff confirm conversion, the admitted lead stays linked to the new student profile used for class allocation and fee setup.",
  }),
  Object.freeze({
    id: "p3",
    title: "Aira operates inside permissions",
    desc: "Aira can answer from permission-checked school data and propose supported next actions; authorised people confirm material changes.",
  }),
  Object.freeze({
    id: "p4",
    title: "A teacher sees their section — nothing else",
    desc: "A teacher reaches only the class-sections they actually teach, resolved from the timetable and their teaching assignments. Each school's data stays its own, money steps need a second approver, and every change leaves audit evidence.",
  }),
  Object.freeze({
    id: "p5",
    title: "See every campus, keep every school separate",
    desc: "Group & Chain adds member-school views and cross-campus controls where it is included in the plan and legal-entity model.",
  }),
  Object.freeze({
    id: "p6",
    title: "You always know what's live and what's coming",
    desc: "Migration coverage, providers, training, support and early-access native mobile availability are confirmed before go-live.",
  }),
]);

const PROOF_GROUPS = Object.freeze([
  Object.freeze({
    label: "Connected record",
    summary: "The school structure and the hand-off use the same working context.",
    Icon: Network,
  }),
  Object.freeze({
    label: "Governed action",
    summary: "Permissions and human checkpoints remain visible around sensitive work.",
    Icon: ShieldCheck,
  }),
  Object.freeze({
    label: "Rollout clarity",
    summary: "Scope, dependencies and responsibilities are agreed before go-live.",
    Icon: ClipboardCheck,
  }),
]);

function SafeReveal({ children, className = "", delay = 0 }) {
  const reducedMotion = useReducedMotion();
  const { paused } = useMarketingMotion();
  const motionOff = Boolean(reducedMotion || paused);

  return (
    <m.div
      className={className}
      initial={motionOff ? false : { transform: "translate3d(0, 10px, 0)" }}
      whileInView={motionOff ? undefined : { transform: "translate3d(0, 0, 0)" }}
      viewport={motionOff ? undefined : { once: true, amount: 0.16 }}
      transition={{ duration: motionOff ? 0 : 0.24, delay: motionOff ? 0 : delay, ease: EASE_OUT }}
    >
      {children}
    </m.div>
  );
}

function useDesktopProofLayout() {
  const readMatch = () => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return true;
    return window.matchMedia("(min-width: 768px)").matches;
  };
  const [desktop, setDesktop] = React.useState(readMatch);

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia("(min-width: 768px)");
    const update = () => setDesktop(media.matches);
    update();
    if (typeof media.addEventListener === "function") media.addEventListener("change", update);
    else media.addListener?.(update);
    return () => {
      if (typeof media.removeEventListener === "function") media.removeEventListener("change", update);
      else media.removeListener?.(update);
    };
  }, []);

  return desktop;
}

export default function WhyVidyapeethSection({ content, onBookDemo }) {
  const reducedMotion = useReducedMotion();
  const { paused } = useMarketingMotion();
  const motionOff = Boolean(reducedMotion || paused);
  const desktopProofLayout = useDesktopProofLayout();
  const [openProofGroup, setOpenProofGroup] = React.useState(null);
  const c = content || {};
  const pillars = Array.isArray(c.pillars) && c.pillars.length
    ? c.pillars.slice(0, 6)
    : FALLBACK_PILLARS;
  const groupedPillars = PROOF_GROUPS
    .map((group, index) => ({ ...group, pillars: pillars.slice(index * 2, index * 2 + 2) }))
    .filter((group) => group.pillars.length);

  return (
    <section
      id="why"
      className="relative scroll-mt-24 overflow-hidden border-b border-line campus-glass-surface"
      aria-labelledby="why-vp360-heading"
      data-testid="why-vidyapeeth360"
      data-aira-fab-clearance="hide-mobile"
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/45 to-transparent" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5 py-20 sm:py-24 lg:py-28">
        <SafeReveal className="grid gap-7 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <p className="inline-flex min-h-8 items-center gap-2 rounded-full border border-accent/20 bg-primary/10 px-3 text-xs font-bold uppercase tracking-[0.14em] text-accent">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
              <BrandCopy>{c.badge || FALLBACK_CONTENT.badge}</BrandCopy>
            </p>
            <h2 id="why-vp360-heading" className="mt-5 max-w-[17ch] text-balance font-heading text-3xl font-bold leading-[1.05] tracking-[-0.028em] text-ink sm:text-4xl lg:text-[3.4rem]">
              <BrandCopy>{c.heading || FALLBACK_CONTENT.heading}</BrandCopy>
            </h2>
          </div>
          <p className="max-w-[58ch] text-pretty text-base leading-relaxed text-ink-2 sm:text-lg lg:col-span-5 lg:pb-1">
            <BrandCopy>{c.subhead || FALLBACK_CONTENT.subhead}</BrandCopy>
          </p>
        </SafeReveal>

        <SafeReveal className="mt-12" delay={0.04}>
            <ConnectedSchoolConceptVisual onRequestTour={onBookDemo} />
        </SafeReveal>

        <div className="mt-14 border-y border-line" aria-label="What you can verify before rollout">
          <SafeReveal className="grid gap-5 py-7 lg:grid-cols-12 lg:items-end" delay={0.06}>
            <div className="lg:col-span-5">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-accent">What you can verify</p>
              <h3 className="mt-2 max-w-[22ch] font-heading text-2xl font-bold leading-tight tracking-tight text-ink sm:text-[1.75rem]">
                The operating model, before rollout.
              </h3>
            </div>
            <p className="max-w-[66ch] text-sm leading-relaxed text-ink-2 lg:col-span-7">
              Evidence is more useful than a feature claim. These are the records, controls and delivery boundaries your team can examine in the product and written proposal.
            </p>
          </SafeReveal>

          <ol
            className="grid border-t border-line md:grid-cols-3 md:divide-x md:divide-line"
            aria-label="Vidyapeeth360 operating-model evidence"
            data-testid="why-proof-rail"
          >
            {groupedPillars.map((group, groupIndex) => {
              const Icon = group.Icon;
              return (
                <m.li
                  key={group.label}
                  className="border-b border-line last:border-b-0 md:border-b-0 md:px-6 md:first:pl-0 md:last:pr-0 lg:px-8"
                  initial={motionOff ? false : { opacity: 0, transform: "translate3d(0, 8px, 0)" }}
                  whileInView={motionOff ? undefined : { opacity: 1, transform: "translate3d(0, 0, 0)" }}
                  viewport={motionOff ? undefined : { once: true, amount: 0.3 }}
                  transition={{ duration: motionOff ? 0 : 0.24, delay: motionOff ? 0 : groupIndex * 0.045, ease: EASE_OUT }}
                >
                  <details
                    open={desktopProofLayout || openProofGroup === groupIndex}
                    data-testid={`why-proof-group-${groupIndex + 1}`}
                  >
                    <summary
                      onClick={(event) => {
                        event.preventDefault();
                        if (!desktopProofLayout) {
                          setOpenProofGroup((current) => (current === groupIndex ? null : groupIndex));
                        }
                      }}
                      className={cn(
                        "flex min-h-16 cursor-pointer list-none items-start justify-between gap-4 py-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focusring/60 [&::-webkit-details-marker]:hidden md:cursor-default md:py-6",
                      )}
                    >
                      <span className="flex items-start gap-3">
                        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-accent" strokeWidth={1.8} aria-hidden="true" />
                        <span>
                          <span className="block text-[11px] font-bold uppercase tracking-[0.13em] text-accent">{group.label}</span>
                          <span className="mt-1.5 block text-[13px] leading-relaxed text-ink-2 sm:text-sm">{group.summary}</span>
                        </span>
                      </span>
                      <ChevronDown
                        className={cn(
                          "mt-0.5 h-4 w-4 shrink-0 text-ink-subtle md:hidden",
                          openProofGroup === groupIndex && "rotate-180",
                        )}
                        aria-hidden="true"
                      />
                    </summary>
                  <div className="divide-y divide-line border-t border-line">
                    {group.pillars.map((pillar, itemIndex) => {
                      const index = groupIndex * 2 + itemIndex;
                      return (
                        <article
                          key={pillar.id || `${pillar.title}-${index}`}
                          className="py-3.5 md:py-4"
                          data-testid={`why-proof-${pillar.id || index + 1}`}
                        >
                          <div className="flex items-baseline justify-between gap-3">
                            <h4 className="font-heading text-[14px] font-bold leading-snug text-ink">
                              <BrandCopy>{pillar.title}</BrandCopy>
                            </h4>
                            <span className="shrink-0 font-mono text-[9px] font-semibold tracking-[0.12em] text-ink-subtle">{String(index + 1).padStart(2, "0")}</span>
                          </div>
                          <p className="mt-2 text-[12.5px] leading-relaxed text-ink-2">
                            <BrandCopy>{pillar.desc}</BrandCopy>
                          </p>
                        </article>
                      );
                    })}
                  </div>
                  </details>
                </m.li>
              );
            })}
          </ol>
        </div>

        <div className="mt-6 grid gap-5 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
          <div className="flex items-start gap-3">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-accent" strokeWidth={1.8} aria-hidden="true" />
            <p className="max-w-[70ch] text-xs leading-relaxed text-ink-subtle">
              Plan availability, provider dependencies, migration responsibilities and rollout scope are confirmed in your written proposal.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-semibold">
              <Link to="/security" className="inline-flex min-h-11 items-center gap-2 text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring/60">
                Review security controls <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link to="/modules" className="inline-flex min-h-11 items-center gap-2 text-ink-2 hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring/60">
                Explore the module catalogue <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export { FALLBACK_CONTENT, FALLBACK_PILLARS };
